/** @jsxImportSource @emotion/react */
import React, { memo, useRef, useCallback } from 'react'
import {
  IconContainer,
  TextContainer,
  MenuItemContainer,
  SubItemsContainer,
  SubItem,
  MenuItemWrapper,
  ArrowContainer,
  SubItemArrowContainer
} from './styles'
import Typography from '@signal/components/Typography'
import { Menu, ArrowForwardIos } from '@material-ui/icons'
import { MenuServiceSubItemType } from '@signal/core/menuService/types'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import stripDashesFromAnyGuids from '@signal/utils/stripDashesFromGuid'
import stripQueryParams from '@signal/utils/stripQueryParams'
import { AnimatePresence, motion } from 'framer-motion'
import { motionStyled } from '../styles'
import { followMenuLink, getMenuItemHref, preventLinkFollow } from '../utils'
import { useDarkModeContext } from '@signal/theme/Context/dark-mode'

// Create motion-enabled versions of styled components
const MotionSubItem = motionStyled(SubItem)
const MotionMenuItemWrapper = motionStyled(MenuItemWrapper)
const MotionSubItemsContainer = motionStyled(SubItemsContainer)

const MenuItemContainerWithChildren = MenuItemContainer as React.ComponentType<
  React.PropsWithChildren<
    React.HTMLAttributes<HTMLDivElement> & {
      $hasActiveSubItem?: boolean
      $isExpanded?: boolean
      $isDarkMode?: boolean
    }
  >
>

const TextContainerWithChildren = TextContainer as React.ComponentType<
  React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>
>

const ArrowContainerWithChildren = ArrowContainer as React.ComponentType<
  React.PropsWithChildren<
    React.HTMLAttributes<HTMLDivElement> & {
      $hasActiveSubItem?: boolean
      $isOpen?: boolean
    }
  >
>

const SubItemArrowContainerWithChildren =
  SubItemArrowContainer as React.ComponentType<
    React.PropsWithChildren<
      React.HTMLAttributes<HTMLDivElement> & {
        $hasSubItems?: boolean
        $isOpen?: boolean
        $isParentActive?: boolean
      }
    >
  >

interface MenuItemProps {
  name: string
  open?: boolean
  onClick?: () => void
  Icon?: React.ComponentType<Record<string, unknown>>
  subMenuItems?: Array<MenuServiceSubItemType>
  onSubItemClick?: (link: string) => void
  currentPathname?: string
  layoutId?: string
  getProcessedLink?: (link: string) => string
  isExpanded?: boolean
  onPopupShow?: (position: { top: number; left: number }) => void
  onPopupHide?: () => void
  searchExpandedSubItems?: Set<string>
}

const MenuItem: React.FC<MenuItemProps> = ({
  name,
  open = false,
  onClick,
  Icon,
  subMenuItems = [],
  onSubItemClick,
  currentPathname = '',
  layoutId,
  getProcessedLink,
  isExpanded = true,
  onPopupShow,
  onPopupHide,
  searchExpandedSubItems = new Set()
}) => {
  const { isDarkMode } = useDarkModeContext()
  // Use provided Icon or fallback to Menu icon
  const IconComponent = Icon || Menu

  // State to track which sub-items are expanded (for third-level items)
  const [expandedSubItems, setExpandedSubItems] = React.useState<Set<string>>(
    new Set()
  )

  // Determine which sub-menu item is active (including third-level items)
  const getActiveSubMenuLink = () => {
    if (!currentPathname || subMenuItems.length === 0) return null

    const normalizedPathname = stripDashesFromAnyGuids(currentPathname)

    // First check second-level items
    const exactMatch = subMenuItems.find(
      ({ link }: MenuServiceSubItemType) =>
        normalizedPathname === stripDashesFromAnyGuids(stripQueryParams(link))
    )

    if (exactMatch) return exactMatch.link

    // Then check third-level items
    for (const subItem of subMenuItems) {
      if (subItem.subMenuItems) {
        const thirdLevelMatch = subItem.subMenuItems.find(
          ({ link }: MenuServiceSubItemType) =>
            normalizedPathname ===
            stripDashesFromAnyGuids(stripQueryParams(link))
        )
        if (thirdLevelMatch) return thirdLevelMatch.link
      }
    }

    // Then look for path that starts with any link (second level)
    const startsWithMatch = subMenuItems.find(
      ({ link }: MenuServiceSubItemType) =>
        normalizedPathname.startsWith(
          stripDashesFromAnyGuids(stripQueryParams(link))
        )
    )

    if (startsWithMatch) return startsWithMatch.link

    // Finally check third-level items for starts-with match
    for (const subItem of subMenuItems) {
      if (subItem.subMenuItems) {
        const thirdLevelStartsWithMatch = subItem.subMenuItems.find(
          ({ link }: MenuServiceSubItemType) =>
            normalizedPathname.startsWith(
              stripDashesFromAnyGuids(stripQueryParams(link))
            )
        )
        if (thirdLevelStartsWithMatch) return thirdLevelStartsWithMatch.link
      }
    }

    return null
  }

  const activeSubMenuLink = getActiveSubMenuLink()
  const hasActiveSubItem = activeSubMenuLink !== null

  // Helper to check if a sub-item has an active third-level item
  const hasActiveThirdLevelItem = (subItem: MenuServiceSubItemType) => {
    if (!subItem.subMenuItems || subItem.subMenuItems.length === 0) return false

    const normalizedPathname = stripDashesFromAnyGuids(currentPathname)
    return subItem.subMenuItems.some(
      ({ link }: MenuServiceSubItemType) =>
        normalizedPathname ===
          stripDashesFromAnyGuids(stripQueryParams(link)) ||
        normalizedPathname.startsWith(
          stripDashesFromAnyGuids(stripQueryParams(link))
        )
    )
  }

  // Helper to determine if a sub-item should be highlighted (active itself OR has active third-level item)
  const isSubItemActive = (subItem: MenuServiceSubItemType) => {
    return (
      subItem.link === activeSubMenuLink || hasActiveThirdLevelItem(subItem)
    )
  }

  const menuItemRef = useRef<HTMLDivElement>(null)
  const hasMountedRef = useRef(false)

  React.useEffect(() => {
    hasMountedRef.current = true
  }, [])

  const textEnterAnimation = {
    opacity: 0,
    width: 0,
    scale: 0.5,
    marginRight: 0,
    x: -10,
    height: 0
  }

  const subMenuEnterAnimation = {
    opacity: 0,
    scaleY: 0
  }

  const subItemEnterAnimation = {
    opacity: 0,
    y: 15
  }

  // Handle mouse events for popup in collapsed mode
  const handleMouseEnter = useCallback(() => {
    if (!isExpanded && menuItemRef.current && onPopupShow) {
      const rect = menuItemRef.current.getBoundingClientRect()
      const position = {
        top: rect.top,
        left: rect.right + 8 // Small gap between menu and popup
      }
      onPopupShow(position)
    }
  }, [isExpanded, onPopupShow])

  const handleMouseLeave = useCallback(() => {
    if (!isExpanded && onPopupHide) {
      onPopupHide()
    }
  }, [isExpanded, onPopupHide])

  // Handle sub-item expansion toggle
  const handleSubItemToggle = useCallback(
    (subItemKey: string, event: React.MouseEvent) => {
      event.preventDefault() // Don't follow the sub-item's link
      event.stopPropagation() // Prevent parent click
      setExpandedSubItems(prev => {
        const newSet = new Set(prev)
        if (newSet.has(subItemKey)) {
          newSet.delete(subItemKey)
        } else {
          newSet.add(subItemKey)
        }
        return newSet
      })
    },
    []
  )

  // the arrow's own click handler stops propagation, so a click on it never
  // reaches the entry's anchor
  const handleItemClick = useCallback(
    (item: MenuServiceSubItemType) => (e: React.MouseEvent) =>
      followMenuLink(e, item.link, getProcessedLink, onSubItemClick),
    [getProcessedLink, onSubItemClick]
  )

  const handleSubItemArrowClick = useCallback(
    (subItemKey: string) => (e: React.MouseEvent) => {
      handleSubItemToggle(subItemKey, e)
    },
    [handleSubItemToggle]
  )

  return (
    <MotionMenuItemWrapper
      ref={menuItemRef}
      layout="position"
      layoutId={layoutId}
      transition={{
        layout: {
          duration: 0.3,
          ease: 'easeInOut'
        }
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <MenuItemContainerWithChildren
        onClick={onClick}
        $hasActiveSubItem={hasActiveSubItem}
        $isExpanded={isExpanded}
        $isDarkMode={isDarkMode}
      >
        <IconContainer>
          <IconComponent />
        </IconContainer>

        {/* Text container with animation for collapsed mode */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              key={`text-container-${name}`}
              animate={{
                // opacity: isExpanded ? 1 : 0,
                // width: isExpanded ? 120 : 0,
                // scale: isExpanded ? 1 : 0.5,
                // marginRight: 0,
                // x: isExpanded ? 0 : -10,
                // height: isExpanded ? 20 : 0,
                opacity: 1,
                width: 120,
                scale: 1,
                marginRight: 0,
                x: 0,
                height: 20
              }}
              initial={hasMountedRef.current ? textEnterAnimation : false}
              exit={{
                opacity: 0,
                width: 0,
                scale: 0.5,
                marginRight: 0,
                x: -10,
                height: 0
              }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              <TextContainerWithChildren className="textContainer">
                <Typography color="textPrimary" variant="body4">
                  {name}
                </Typography>
              </TextContainerWithChildren>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Arrow container with animation for collapsed mode */}
        {subMenuItems.length > 0 && isExpanded && (
          <motion.div
            className="arrowContainer"
            animate={{
              opacity: isExpanded ? 1 : 0,
              width: isExpanded ? 'auto' : 0
            }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <ArrowContainerWithChildren
              $hasActiveSubItem={hasActiveSubItem}
              $isOpen={open}
            >
              <ArrowForwardIos />
            </ArrowContainerWithChildren>
          </motion.div>
        )}
      </MenuItemContainerWithChildren>
      {/* Only show expanded sub-items when sidebar is expanded */}
      {isExpanded && (open || hasActiveSubItem) && subMenuItems.length > 0 && (
        <MotionSubItemsContainer
          initial={hasMountedRef.current ? subMenuEnterAnimation : false}
          animate={{
            opacity: 1,
            scaleY: 1,
            transition: {
              opacity: { duration: 0.2, delay: 0.05 },
              scaleY: { duration: 0.25, ease: 'easeOut' }
            }
          }}
          exit={{
            opacity: 0,
            scaleY: 0,
            transition: {
              scaleY: { duration: 0.2, ease: 'easeIn' },
              opacity: { duration: 0.15 }
            }
          }}
          style={{ overflow: 'hidden', transformOrigin: 'top' }}
        >
          {subMenuItems
            .filter((subItem: MenuServiceSubItemType) => {
              // If menu is open, show all items
              // If menu is closed but has active item, show only active items (including parents of active third-level items)
              return open || isSubItemActive(subItem)
            })
            .map((subItem: MenuServiceSubItemType, index: number) => {
              const isActive = isSubItemActive(subItem) // Use new helper that includes parent highlighting
              const hasThirdLevelItems =
                subItem.subMenuItems && subItem.subMenuItems.length > 0
              const isUserExpanded = expandedSubItems.has(
                subItem.link || subItem.name
              )
              const subItemKey = subItem.link || subItem.name
              const href = getMenuItemHref(subItem.link, getProcessedLink)

              return (
                <React.Fragment key={'sub-menu-fragment-' + subItemKey}>
                  {/* Second-level item */}
                  <MotionSubItem
                    animate={{
                      opacity: 1,
                      y: 0
                    }}
                    transition={{
                      duration: 0.2,
                      delay: index * 0.05,
                      ease: 'easeOut'
                    }}
                    initial={
                      hasMountedRef.current ? subItemEnterAnimation : false
                    }
                    key={'sub-menu-item-' + subItemKey}
                    href={href}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={handleItemClick(subItem)}
                    $isActive={isActive}
                    $indentLevel={1}
                    style={{ cursor: href ? 'pointer' : 'default' }}
                  >
                    <Typography
                      color={isActive ? 'primary' : 'textSecondary'}
                      variant="body5"
                    >
                      {subItem.name}
                    </Typography>

                    {/* Arrow for sub-items that have third-level items */}
                    {hasThirdLevelItems && (
                      <SubItemArrowContainerWithChildren
                        className="sub-item-arrow"
                        onAuxClick={preventLinkFollow}
                        $hasSubItems={hasThirdLevelItems}
                        $isOpen={isUserExpanded}
                        $isParentActive={isActive}
                        onClick={handleSubItemArrowClick(subItemKey)}
                      >
                        <ArrowForwardIos />
                      </SubItemArrowContainerWithChildren>
                    )}
                  </MotionSubItem>

                  {/* Third-level items */}
                  {hasThirdLevelItems &&
                    (isUserExpanded ||
                      hasActiveThirdLevelItem(subItem) ||
                      searchExpandedSubItems.has(subItemKey)) && (
                      <AnimatePresence>
                        {subItem
                          .subMenuItems!.filter(
                            (thirdLevelItem: MenuServiceSubItemType) => {
                              // If user has expanded or search has expanded, show all items
                              // If collapsed but has active items, show only active items
                              return (
                                isUserExpanded ||
                                searchExpandedSubItems.has(subItemKey) ||
                                thirdLevelItem.link === activeSubMenuLink
                              )
                            }
                          )
                          .map(
                            (
                              thirdLevelItem: MenuServiceSubItemType,
                              thirdIndex: number
                            ) => {
                              const isThirdLevelActive =
                                thirdLevelItem.link === activeSubMenuLink
                              const thirdLevelHref = getMenuItemHref(
                                thirdLevelItem.link,
                                getProcessedLink
                              )

                              return (
                                <MotionSubItem
                                  animate={{
                                    opacity: 1,
                                    y: 0
                                  }}
                                  transition={{
                                    duration: 0.2,
                                    delay: index * 0.05 + thirdIndex * 0.03,
                                    ease: 'easeOut'
                                  }}
                                  initial={
                                    hasMountedRef.current
                                      ? subItemEnterAnimation
                                      : false
                                  }
                                  exit={{
                                    opacity: 0,
                                    y: -15,
                                    transition: { duration: 0.15 }
                                  }}
                                  key={
                                    'third-level-item-' +
                                    thirdLevelItem.link +
                                    thirdLevelItem.name
                                  }
                                  href={thirdLevelHref}
                                  aria-current={
                                    isThirdLevelActive ? 'page' : undefined
                                  }
                                  onClick={handleItemClick(thirdLevelItem)}
                                  $isActive={isThirdLevelActive}
                                  $indentLevel={2}
                                  style={{
                                    cursor: thirdLevelHref
                                      ? 'pointer'
                                      : 'default'
                                  }}
                                >
                                  <Typography
                                    color={
                                      isThirdLevelActive
                                        ? 'primary'
                                        : 'textSecondary'
                                    }
                                    variant="body5"
                                  >
                                    {thirdLevelItem.name}
                                  </Typography>
                                </MotionSubItem>
                              )
                            }
                          )}
                      </AnimatePresence>
                    )}
                </React.Fragment>
              )
            })}
        </MotionSubItemsContainer>
      )}
    </MotionMenuItemWrapper>
  )
}

export default memo(withCommonSnapshotsOnly(MenuItem))
